import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, ArrowRight, BookOpen } from "lucide-react";

const articles = [
  {
    title: "Planning a Bathroom Remodel in Houston? 7 Mistakes to Avoid",
    excerpt: "From poor ventilation to choosing the wrong tile, here's what we see most often — and how to avoid costly do-overs.",
    category: "Bathroom",
    date: "Updated 2025",
    readTime: "5 min read",
  },
  {
    title: "How Much Does a Kitchen Remodel Really Cost in Houston?",
    excerpt: "A realistic breakdown of budget ranges, where your money goes, and how to prioritize your wishlist.",
    category: "Kitchen",
    date: "Updated 2025",
    readTime: "7 min read",
  },
  {
    title: "Houston Humidity and Your Home: Materials That Last",
    excerpt: "Why some flooring, cabinets, and deck materials fail early — and what we recommend instead.",
    category: "Materials",
    date: "2025",
    readTime: "4 min read",
  },
  {
    title: "What to Expect During a 6-Week Renovation",
    excerpt: "A week-by-week guide to our typical renovation timeline so you're never in the dark.",
    category: "Planning",
    date: "2025",
    readTime: "6 min read",
  },
];

export default function BlogSection() {
  return (
    <section id="blog" className="py-16 md:py-24 relative overflow-hidden" data-testid="section-blog">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#00d9ff]/5 to-transparent pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        <div className="text-center mb-12 md:mb-16">
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold gradient-text mb-4 tracking-tight"
            data-testid="text-blog-title"
          >
            Expert Tips for Houston Homeowners
          </h2>
          <p
            className="text-lg md:text-xl text-[#e0e0e0]/70 max-w-2xl mx-auto"
            data-testid="text-blog-subtitle"
          >
            Insights and guidance from our experienced team to help with your home improvement projects
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          {articles.map((article, index) => (
            <Card
              key={index}
              className="group glass-card hover-lift hover-glow-cyan transition-all duration-500 cursor-pointer flex flex-col"
              data-testid={`card-article-${index}`}
            >
              <div className="p-6 md:p-8 flex-1 flex flex-col">
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-[#00d9ff]/20 text-[#00d9ff] border-[#00d9ff]/30" data-testid={`badge-category-${index}`}>
                    {article.category}
                  </Badge>
                </div>
                <h3
                  className="text-xl md:text-2xl font-heading font-semibold text-[#00d9ff] mb-3 group-hover:text-[#ffd700] transition-colors line-clamp-2"
                  data-testid={`text-article-title-${index}`}
                >
                  {article.title}
                </h3>
                <p
                  className="text-[#e0e0e0]/70 leading-relaxed mb-4 line-clamp-3 flex-1"
                  data-testid={`text-article-excerpt-${index}`}
                >
                  {article.excerpt}
                </p>
                <div className="flex items-center justify-between text-sm text-gray-400 pt-4 border-t border-gray-700/50">
                  <div className="flex items-center gap-2" data-testid={`text-article-date-${index}`}>
                    <Calendar className="h-4 w-4" />
                    <span>{article.date}</span>
                  </div>
                  <div className="flex items-center gap-2" data-testid={`text-article-readtime-${index}`}>
                    <BookOpen className="h-4 w-4" />
                    <span>{article.readTime}</span>
                  </div>
                </div>
              </div>
              <div className="px-6 pb-6">
                <Button
                  variant="ghost"
                  className="w-full justify-between group/btn text-[#00d9ff] hover:text-[#ffd700] hover:bg-[#00d9ff]/10 transition-all duration-300"
                  data-testid={`button-read-article-${index}`}
                >
                  <span>Read Article</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                </Button>
              </div>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button size="lg" variant="outline" data-testid="button-view-all-articles">
            View All Articles
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
}
